import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { ThemeModeSwitcher } from '../../../_metronic/partials/layout/theme-mode/ThemeModeSwitcher';
import { useEmailPanel } from '../../pages/mail/contexts/EmailPanelContext';
import { useWebsiteLeads } from '../../contexts/WebsiteLeadsContext';
import NewCustomerModal from '../../pages/customers/NewCustomerModal';
import NewJobModal from '../../pages/job-summary/NewJobModal';
import PasswordManagerModal from '../../modals/PasswordManagerModal';
import AgentAccessModal from '../../modals/AgentAccessModal';
import { getAgentAccessConfig } from '../../pages/marketing/googleAdsApi';
import { getAuth, onIdTokenChanged } from 'firebase/auth';
import { useAndreiChat } from '../../contexts/AndreiChatContext';
import { useApprovals } from '../../contexts/ApprovalsContext';
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { RecentConversationsPopover } from './RecentConversationsPopover';

interface TerminalHeaderProps {
  /** Called when the mobile hamburger is tapped. */
  onMenuToggle: () => void;
  /** Indicates the aside is currently open on mobile (for aria + icon state). */
  mobileMenuOpen: boolean;
}

/**
 * Renders the path portion of the prompt: `~/dashboard`, `~/accounting`, etc.
 * First two path segments only — deeper routes would wrap and defeat the clean
 * prompt feel. Falls back to `~` for the root.
 */
function pathBreadcrumb(pathname: string): string {
  const clean = pathname.replace(/^\/+|\/+$/g, '');
  if (!clean) return '~';
  const parts = clean.split('/').slice(0, 2);
  return '~/' + parts.join('/');
}

/** Small live clock on the right of the header. Updates every 30s. */
function useClockText(): string {
  const [text, setText] = useState<string>(() => formatClock(new Date()));
  useEffect(() => {
    const tick = () => setText(formatClock(new Date()));
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, []);
  return text;
}

function formatClock(d: Date): string {
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/**
 * Terminal header action — bracketed text button, optionally with a count
 * badge suffixed in parens: [mail (12)]. Single source of truth for the
 * clicky chromed-text look so every button is uniform.
 */
interface ActionProps {
  label: string;
  onClick: () => void;
  count?: number;
  /** 'primary' (green, used for non-zero counts), 'amber' (leads-style), undefined = default. */
  countTone?: 'primary' | 'amber' | 'danger';
  title?: string;
  id?: string;
}
const Action = React.forwardRef<HTMLButtonElement, ActionProps>(
  ({ label, onClick, count, countTone, title, id }, ref) => (
    <button
      ref={ref}
      type="button"
      id={id}
      className="term-header__action"
      onClick={onClick}
      title={title || label}
    >
      <span className="term-header__action-bracket">[</span>
      <span className="term-header__action-label">{label}</span>
      {count !== undefined && count > 0 && (
        <span
          className={clsx('term-header__action-count', {
            'term-header__action-count--primary': countTone === 'primary',
            'term-header__action-count--amber':   countTone === 'amber',
            'term-header__action-count--danger':  countTone === 'danger',
          })}
        >
          {count}
        </span>
      )}
      <span className="term-header__action-bracket">]</span>
    </button>
  ),
);
Action.displayName = 'Action';

const TerminalHeader: React.FC<TerminalHeaderProps> = ({ onMenuToggle, mobileMenuOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = pathBreadcrumb(location.pathname);
  const clock = useClockText();

  // Context data — same hooks the classic HeaderWrapper uses, so counts
  // and active states stay in sync whichever theme is rendered.
  // (The legacy chat unread badge was retired 2026-06 — it never lit anyway;
  //  the [chat] action opens the Andrei RecentConversationsPopover.)
  const { openEmailPanel, unreadBadgeCount, activeSyncJob } = useEmailPanel();
  const { newLeadsCount, openLeadsPanel } = useWebsiteLeads();

  // Modals
  const [showNewJob, setShowNewJob] = useState(false);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [showAgentAccess, setShowAgentAccess] = useState(false);
  const [showMobileOverflow, setShowMobileOverflow] = useState(false);
  // Owner-only: the [access] action shows only for the platform owner
  // (getAgentAccessConfig is a requireOwner endpoint — the same gate the panel uses).
  const [isAgentAccessOwner, setIsAgentAccessOwner] = useState(false);
  // Approvals inbox "review →" on a pending broker grant deep-links here to open
  // the grant-approval console (TTL + scope review) — never a one-tap approve.
  useEffect(() => {
    const handler = () => setShowAgentAccess(true);
    window.addEventListener('approvals:open-agent-access', handler);
    return () => window.removeEventListener('approvals:open-agent-access', handler);
  }, []);
  // Recent-conversations popover (replaces legacy DrawerMessenger
  // since 2026-05). Anchored to the [chat] button via a ref.
  const [showRecentConvos, setShowRecentConvos] = useState(false);
  const chatBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const [chatAnchorRect, setChatAnchorRect] = useState<DOMRect | null>(null);
  const toggleRecentConvos = () => {
    const next = !showRecentConvos;
    if (next && chatBtnRef.current) {
      setChatAnchorRect(chatBtnRef.current.getBoundingClientRect());
    }
    setShowRecentConvos(next);
  };
  // Header [andrei] button opens the chat panel via the global context
  // (mounted at App level so any page can call openAndreiChat with its own
  // context handle). Admin tabs (pending/activity/settings) live in
  // Settings → Andrei Agent card, which opens the AndreiApprovalPanel
  // modal locally.
  const { openAndreiChat } = useAndreiChat();
  // Unified approvals inbox — count flashes (tone → danger) when a new item lands.
  const { openApprovals, count: approvalsCount, flash: approvalsFlash } = useApprovals();

  // Pending-action count for the [andrei N] badge. Live subscription;
  // cap at 50 docs so this stays cheap. Same `agent_pending_actions`
  // collection that fed the old Steve flow — Andrei's queued tool calls
  // continue to land here.
  const [pendingCount, setPendingCount] = useState<number>(0);
  useEffect(() => {
    const q = query(
      collection(db, 'agent_pending_actions'),
      where('status', '==', 'pending'),
      limit(50),
    );
    const unsub = onSnapshot(q, (snap) => {
      setPendingCount(snap.size);
    }, () => { /* rules might not be set up yet; silent */ });
    return () => unsub();
  }, []);

  // The calendar is a real route now (`/calendar`), so the old
  // `open-calendar-modal` window-event bridge is gone: it existed only because
  // the calendar could not be navigated to. Anything that used to dispatch it
  // navigates instead.
  useEffect(() => {
    const handler = () => navigate('/calendar');
    window.addEventListener('open-calendar-modal', handler);
    return () => window.removeEventListener('open-calendar-modal', handler);
  }, [navigate]);

  // Resolve owner status once a Firebase user/token exists (authFetch needs one).
  useEffect(() => {
    const unsub = onIdTokenChanged(getAuth(), async (u) => {
      if (!u) { setIsAgentAccessOwner(false); return; }
      try { const r = await getAgentAccessConfig(); setIsAgentAccessOwner(!!r?.ok); }
      catch { setIsAgentAccessOwner(false); }
    });
    return () => unsub();
  }, []);

  // Legacy DrawerMessenger is still mounted (so Settings → Legacy
  // Chats can open it) but the header [chat] button no longer
  // toggles it; it now opens the Recent Conversations popover.
  // Kept for reference: openChatDrawer used DrawerComponent.getInstance(
  // 'kt_drawer_chat').show() / hide().

  // Sync progress (reusing the same hook, just rendered terminal-style).
  const syncProgressPct =
    activeSyncJob?.status === 'completed'
      ? 100
      : activeSyncJob?.percent ?? (activeSyncJob?.status === 'pending' ? 10 : 60);

  return (
    <>
      <header className="term-header">
        {/* Sync progress bar — lives above the header row so it doesn't shift layout */}
        {activeSyncJob && (
          <div
            className={clsx('term-header__sync', `term-header__sync--${activeSyncJob.status}`)}
            style={{ width: `${syncProgressPct}%` }}
            title={activeSyncJob.progress || 'Syncing mail...'}
          />
        )}

        <div className="term-header__left">
          <button
            type="button"
            className="term-header__menu"
            onClick={onMenuToggle}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? '×' : '≡'}
          </button>
          <span className="term-header__host">~</span>
          <span className="term-header__sigil">$</span>
          <span className="term-header__path">{path}</span>
          <span className="term-header__cursor" aria-hidden="true">_</span>
        </div>

        <div className="term-header__right">
          {/* Desktop actions (hidden below md) */}
          <div className="term-header__actions term-header__actions--desktop">
            <Action label="+job"   onClick={() => setShowNewJob(true)}      title="New job" />
            <Action label="+cust"  onClick={() => setShowNewCustomer(true)} title="New customer" />
            <Action label="cal"    onClick={() => navigate('/calendar')}    title="Calendar" />
            <Action label="pwd"    onClick={() => setShowPasswords(true)}   title="Password manager" />
            {/* [chat] now opens the Recent Conversations popover
                (anchored via chatBtnRef). Legacy DrawerMessenger is
                still mounted but no longer toggleable from the
                header — see Settings → Legacy Chats. */}
            <Action
              ref={chatBtnRef}
              label="chat"
              onClick={toggleRecentConvos}
              title="Recent conversations"
            />
            <Action
              label="leads"
              onClick={openLeadsPanel}
              count={newLeadsCount}
              countTone="amber"
              title="Website activity"
            />
            <Action
              label="mail"
              onClick={() => openEmailPanel('feed')}
              count={unreadBadgeCount}
              countTone="primary"
              title="Email feed"
            />
            <Action
              label="andrei"
              onClick={() => openAndreiChat()}
              count={pendingCount}
              // Amber when there's anything to approve — it's a "needs
              // your attention" badge, same semantic as leads. Red in
              // the >10 range means Steve's queued up a lot; worth
              // checking why.
              countTone={pendingCount > 10 ? 'danger' : 'amber'}
              title="Chat with Andrei (admin in Settings → Andrei Agent)"
            />
            <Action
              label="approve"
              onClick={openApprovals}
              count={approvalsCount}
              // Flashes red briefly when a new proposal lands; amber otherwise.
              countTone={approvalsFlash ? 'danger' : 'amber'}
              title="Approvals inbox — pending agent proposals (Google Ads, accounting, email drafts)"
            />
            {isAgentAccessOwner && (
              <Action
                label="access"
                onClick={() => setShowAgentAccess(true)}
                title="Agent access — approve external-agent connections (Google Ads + CRM/billing/email)"
              />
            )}
          </div>

          {/* Mobile: single overflow action + hamburger triggers the aside */}
          <button
            type="button"
            className="term-header__overflow"
            onClick={() => setShowMobileOverflow((s) => !s)}
            aria-expanded={showMobileOverflow}
            aria-label="More actions"
          >
            {/* Wrap brackets in spans so hover/focus tint them green
                like every other bracketed action in the terminal UI
                (chrome.scss targets .term-header__action-bracket). */}
            <span className="term-header__action-bracket">[</span>
            <span className="term-header__action-label">
              {showMobileOverflow ? '×' : '…'}
            </span>
            <span className="term-header__action-bracket">]</span>
          </button>

          <span className="term-header__clock" title={new Date().toLocaleString()}>
            {clock}
          </span>

          <div className="term-header__switcher">
            <ThemeModeSwitcher
              toggleBtnClass="term-header__switcher-btn"
              toggleBtnIconClass="svg-icon-3"
            />
          </div>
        </div>

        {/* Mobile overflow sheet — shows the same actions as a stacked list */}
        {showMobileOverflow && (
          <div
            className="term-header__overflow-sheet"
            role="menu"
            onClick={(e) => {
              // close when any action runs
              if ((e.target as HTMLElement).closest('.term-header__action')) {
                setShowMobileOverflow(false);
              }
            }}
          >
            <Action label="+job"   onClick={() => setShowNewJob(true)}      title="New job" />
            <Action label="+cust"  onClick={() => setShowNewCustomer(true)} title="New customer" />
            <Action label="cal"    onClick={() => navigate('/calendar')}    title="Calendar" />
            <Action label="pwd"    onClick={() => setShowPasswords(true)}   title="Password manager" />
            <Action label="chat"   onClick={toggleRecentConvos}            title="Recent conversations" />
            <Action label="leads"  onClick={openLeadsPanel}                 count={newLeadsCount}           countTone="amber" />
            <Action label="mail"   onClick={() => openEmailPanel('feed')}   count={unreadBadgeCount}        countTone="primary" />
            <Action
              label="andrei"
              onClick={() => openAndreiChat()}
              count={pendingCount}
              countTone={pendingCount > 10 ? 'danger' : 'amber'}
              title="Chat with Andrei (admin in Settings → Andrei Agent)"
            />
            <Action
              label="approve"
              onClick={openApprovals}
              count={approvalsCount}
              countTone={approvalsFlash ? 'danger' : 'amber'}
              title="Approvals inbox — pending agent proposals (Google Ads, accounting, email drafts)"
            />
            {isAgentAccessOwner && (
              <Action label="access" onClick={() => setShowAgentAccess(true)} title="Agent access" />
            )}
          </div>
        )}
      </header>

      {/* Modals — reusing the classic modal components so there's a single
          source of truth for their behavior. They render fine on both themes
          because Bootstrap modals use CSS variables that the terminal
          override map already covers. */}
      {showNewJob && <NewJobModal show={showNewJob} onClose={() => setShowNewJob(false)} />}
      {showNewCustomer && (
        <NewCustomerModal
          show={showNewCustomer}
          onClose={() => setShowNewCustomer(false)}
          customer={null}
        />
      )}
      {showPasswords && <PasswordManagerModal onClose={() => setShowPasswords(false)} />}
      {showAgentAccess && <AgentAccessModal onClose={() => setShowAgentAccess(false)} />}

      {/* Recent-conversations popover — anchored under the [chat] button.
          Subscribes to andrei_sessions only while shown. */}
      <RecentConversationsPopover
        show={showRecentConvos}
        onClose={() => setShowRecentConvos(false)}
        anchorRect={chatAnchorRect}
      />
    </>
  );
};

export default TerminalHeader;
